import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField,
  Alert, Typography, Autocomplete, Paper,
} from '@mui/material'
import { Add } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listClientPriceOverrides, addClientPriceOverride, type ClientListItem } from '../../api/clienti'
import { listArticles } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

/** FR-MDP-002 — preț negociat client × articol, nivelul cel mai specific din ierarhia de discount. */
export default function ClientPriceOverridesDialog({ client, onClose }: { client: ClientListItem | null; onClose: () => void }) {
  const open = client !== null
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const { data: overrides } = useQuery({
    queryKey: ['comercial', 'preturi-negociate', client?.id],
    queryFn: () => listClientPriceOverrides(client!.id),
    enabled: open,
  })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })

  const [articleId, setArticleId] = useState<string | null>(null)
  const [price, setPrice] = useState('')
  const [currency, setCurrency] = useState('EUR')
  const [effectiveFrom, setEffectiveFrom] = useState('')

  const mutation = useMutation({
    mutationFn: () => addClientPriceOverride(client!.id, {
      articleId: articleId!, price: Number(price), currency, effectiveFrom, effectiveTo: null,
    }),
    onSuccess: () => {
      setArticleId(null); setPrice(''); setEffectiveFrom('')
      void qc.invalidateQueries({ queryKey: ['comercial', 'preturi-negociate', client?.id] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Prețuri negociate — {client?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            <Autocomplete
              sx={{ flexGrow: 1 }} options={articles?.map((a) => a.id) ?? []}
              getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
              value={articleId} onChange={(_, v) => setArticleId(v)}
              renderInput={(params) => <TextField {...params} label="Articol" size="small" />}
            />
            <TextField size="small" type="number" label="Preț" value={price} onChange={(e) => setPrice(e.target.value)} sx={{ width: 110 }} />
            <TextField size="small" label="Monedă" value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ width: 90 }} />
            <TextField size="small" type="date" label="Valabil de la" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 160 }} />
            <Button variant="outlined" startIcon={<Add />} disabled={!articleId || !price || !effectiveFrom || mutation.isPending}
              onClick={() => mutation.mutate()}>
              Adaugă
            </Button>
          </Stack>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Stack spacing={1}>
            {overrides?.length === 0 && <Typography variant="body2" color="text.secondary">Niciun preț negociat încă.</Typography>}
            {overrides?.map((o) => (
              <Paper key={o.id} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{o.articleCode} — {o.articleDescription}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {o.price} {o.currency} · valabil {o.effectiveFrom}{o.effectiveTo ? ` – ${o.effectiveTo}` : ' (fără dată de sfârșit)'}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}
